<template>
    <el-container>
        <el-header>
            <span>Reports</span>
        </el-header>
        <el-main class="report-main">
            <div class="report-tabs">
                <button class="report-tab active" type="button">Dashboard</button>
                <el-select class="year-select" v-model="selectedYear" @change="queryReportData">
                    <el-option
                            v-for="yearOption in yearOptions"
                            :key="yearOption.key"
                            :label="yearOption.label"
                            :value="yearOption.value"></el-option>
                </el-select>
            </div>

            <section class="report-section report-overview-section" v-loading="loading">
                <div class="report-overview-grid">
                    <div class="report-panel">
                        <h2>Annual sales by client</h2>
                        <div class="client-chart-row">
                            <div class="donut-chart-wrap">
                                <svg class="donut-chart" viewBox="0 0 200 200">
                                    <circle class="donut-empty" cx="100" cy="100" r="70"></circle>
                                    <path
                                            v-for="client in clientPieSliceList"
                                            :key="'pie-' + client.innerUUID"
                                            class="donut-slice"
                                            :fill="client.color"
                                            :d="client.path"
                                            @mouseenter="showClientPieTooltip(client, $event)"
                                            @mousemove="moveClientPieTooltip($event)"
                                            @mouseleave="hideClientPieTooltip"></path>
                                </svg>
                                <div
                                        v-if="clientPieTooltip.visible"
                                        class="client-pie-tooltip"
                                        :style="{left: clientPieTooltip.left + 'px', top: clientPieTooltip.top + 'px'}">
                                    {{clientPieTooltip.content}}
                                </div>
                            </div>
                            <div class="client-sales-list">
                                <div class="client-sales-row" v-for="client in clientSalesList" :key="client.innerUUID">
                                    <span class="client-color" :style="{background: client.color}"></span>
                                    <span class="client-name">{{client.name}} - {{client.percent}}%</span>
                                    <strong>{{formatMoney(client.totalMoney)}}</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="report-panel">
                        <h2>Total outstanding balance</h2>
                        <div class="outstanding-total">{{formatMoney(totalOutstandingBalance)}}</div>
                        <div class="outstanding-list">
                            <div class="outstanding-row" v-for="client in displayOutstandingBalanceList" :key="client.innerUUID">
                                <strong>{{client.name}}</strong>
                                <strong>{{formatMoney(client.totalOutstandingBalance)}}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section class="report-section sales-section" v-loading="loading">
                <div class="report-section-header">
                    <div>
                        <h2>{{saleReportTitle}}</h2>
                        <div class="invoice-count">{{reportSummary.totalInvoiceCount}} invoices</div>
                        <div class="total-sales">{{formatMoney(reportSummary.totalMoney)}}</div>
                    </div>
                </div>

                <div class="report-mode-tabs">
                    <button :class="['mode-button', dateIncrement === 'month' ? 'active' : '']" type="button" @click="changeDateIncrement('month')">By month</button>
                    <button :class="['mode-button', dateIncrement === 'quarter' ? 'active' : '']" type="button" @click="changeDateIncrement('quarter')">By quarter</button>
                </div>

                <div v-if="displayPeriodList.length > 0" class="bar-chart-wrap">
                    <div v-if="selectedYear === null && displayPeriodList.length > 10" class="bar-chart-actions">
                        <el-button size="mini" icon="el-icon-arrow-left" circle @click="scrollSaleChart(-1)"></el-button>
                        <el-button size="mini" icon="el-icon-arrow-right" circle @click="scrollSaleChart(1)"></el-button>
                    </div>
                    <div ref="saleChartScroll" class="bar-chart-scroll" :style="{width: chartViewportWidth}" @wheel="handleSaleChartWheel">
                        <div class="bar-chart" :style="{gridTemplateColumns: chartGridTemplateColumns, width: chartWidth}">
                            <div
                                    v-for="period in displayPeriodList"
                                    :key="period.periodKey"
                                    class="bar-column">
                                <el-tooltip effect="dark" :content="getSaleTooltip(period)" placement="top">
                                    <div class="bar-track">
                                        <div class="bar-value" :style="{height: getBarHeight(period.totalMoney)}"></div>
                                    </div>
                                </el-tooltip>
                                <div class="bar-label">{{period.periodName}}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="empty-report">No sales found</div>

                <div class="period-list">
                    <div class="period-row" v-for="period in displayPeriodTableList" :key="'row-' + period.periodKey">
                        <span>{{period.title}} - {{period.invoiceCount}} invoices</span>
                        <strong>{{formatMoney(period.totalMoney)}}</strong>
                    </div>
                </div>
            </section>

        </el-main>
    </el-container>
</template>

<script>
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {getCurrentCompanyUUID} from '@/utils/company'

    export default {
        data() {
            return {
                loading: false,
                selectedYear: null,
                dateIncrement: "month",
                userSetting: JSON.parse(localStorage.getItem('userSetting')),
                reportSummary: {
                    totalInvoiceCount: 0,
                    totalMoney: 0
                },
                reportBarList: [],
                clientSalesList: [],
                outstandingBalanceList: [],
                loadingCount: 0,
                clientPieTooltip: {
                    visible: false,
                    content: "",
                    left: 0,
                    top: 0
                },
                clientColorList: ["#217BD3", "#31A66A", "#F2A541", "#E05252", "#6B5DD3", "#00A7A7", "#7A5230", "#8C8C8C"]
            }
        },
        computed: {
            yearOptions() {
                let yearList = [{
                    key: "all",
                    label: "All years",
                    value: null
                }];
                let currentYear = this.getCurrentFiscalYearEnd();
                for (let i = currentYear + 1; i >= currentYear - 5; i--) {
                    yearList.push({
                        key: String(i),
                        label: String(i),
                        value: i
                    });
                }
                return yearList;
            },
            saleReportTitle() {
                if (this.selectedYear === null) {
                    return "All sales";
                }
                return "Tax year " + this.selectedYear + " sales";
            },
            groupType() {
                if (this.dateIncrement === "month") {
                    return "MONTH";
                }
                if (this.dateIncrement === "quarter") {
                    return "QUARTER";
                }
                throw new Error("Invalid report group type");
            },
            chartGridTemplateColumns() {
                return "repeat(" + this.displayPeriodList.length + ", minmax(0, 1fr))";
            },
            chartWidth() {
                if (this.selectedYear !== null || this.displayPeriodList.length <= 10) {
                    return "100%";
                }
                return (this.displayPeriodList.length * 10) + "%";
            },
            chartViewportWidth() {
                return "100%";
            },
            displayPeriodList() {
                return this.reportBarList;
            },
            displayPeriodTableList() {
                if (this.selectedYear === null || this.groupType !== "MONTH") {
                    return this.displayPeriodList;
                }
                let tableEndMonthValue = this.getSelectedFiscalTableEndMonthValue();
                if (tableEndMonthValue === null) {
                    return [];
                }
                return this.displayPeriodList.filter(period => {
                    return this.getMonthValue(period.periodYear, period.periodMonth) <= tableEndMonthValue;
                });
            },
            totalOutstandingBalance() {
                let totalMoney = 0;
                for (let i = 0; i < this.outstandingBalanceList.length; i++) {
                    totalMoney += Number(this.outstandingBalanceList[i].totalOutstandingBalance);
                }
                return totalMoney;
            },
            displayOutstandingBalanceList() {
                return this.outstandingBalanceList.slice().sort((firstClient, secondClient) => {
                    return Number(secondClient.totalOutstandingBalance) - Number(firstClient.totalOutstandingBalance);
                }).slice(0, 5);
            },
            clientPieSliceList() {
                let totalMoney = 0;
                for (let i = 0; i < this.clientSalesList.length; i++) {
                    totalMoney += Number(this.clientSalesList[i].totalMoney);
                }
                if (totalMoney === 0) {
                    return [];
                }
                let currentAngle = -90;
                let sliceList = [];
                for (let i = 0; i < this.clientSalesList.length; i++) {
                    let client = this.clientSalesList[i];
                    let sliceAngle = Number(client.totalMoney) / totalMoney * 360;
                    let startAngle = currentAngle;
                    let endAngle = currentAngle + sliceAngle;
                    sliceList.push({
                        innerUUID: client.innerUUID,
                        name: client.name,
                        totalMoney: client.totalMoney,
                        color: client.color,
                        path: this.getDonutSlicePath(100, 100, 70, 42, startAngle, endAngle)
                    });
                    currentAngle = endAngle;
                }
                return sliceList;
            },
            maxReportMoney() {
                let maxMoney = 0;
                for (let i = 0; i < this.displayPeriodList.length; i++) {
                    let totalMoney = parseFloat(this.displayPeriodList[i].totalMoney);
                    if (totalMoney > maxMoney) {
                        maxMoney = totalMoney;
                    }
                }
                return maxMoney;
            },
        },
        methods: {
            formatMoney(value) {
                return xiaoshudianfenge(value, this.userSetting);
            },
            startLoading() {
                this.loadingCount++;
                this.loading = true;
            },
            finishLoading() {
                this.loadingCount--;
                if (this.loadingCount === 0) {
                    this.loading = false;
                }
                if (this.loadingCount < 0) {
                    throw new Error("Report loading count is invalid");
                }
            },
            getBarHeight(value) {
                if (parseFloat(value) <= 0 || this.maxReportMoney === 0) {
                    return "0px";
                }
                return Math.max(16, parseFloat(value) / this.maxReportMoney * 240) + "px";
            },
            getSaleTooltip(period) {
                return period.periodName + " | " + this.formatMoney(period.totalMoney) + " | " + period.invoiceCount + " invoices";
            },
            getClientSaleTooltip(client) {
                return client.name + " | " + this.formatMoney(client.totalMoney);
            },
            getPointOnCircle(centerX, centerY, radius, angle) {
                let angleInRadians = angle * Math.PI / 180;
                return {
                    x: centerX + radius * Math.cos(angleInRadians),
                    y: centerY + radius * Math.sin(angleInRadians)
                };
            },
            getDonutSlicePath(centerX, centerY, outerRadius, innerRadius, startAngle, endAngle) {
                let largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
                let outerStart = this.getPointOnCircle(centerX, centerY, outerRadius, startAngle);
                let outerEnd = this.getPointOnCircle(centerX, centerY, outerRadius, endAngle);
                let innerStart = this.getPointOnCircle(centerX, centerY, innerRadius, startAngle);
                let innerEnd = this.getPointOnCircle(centerX, centerY, innerRadius, endAngle);
                return [
                    "M", outerStart.x, outerStart.y,
                    "A", outerRadius, outerRadius, 0, largeArcFlag, 1, outerEnd.x, outerEnd.y,
                    "L", innerEnd.x, innerEnd.y,
                    "A", innerRadius, innerRadius, 0, largeArcFlag, 0, innerStart.x, innerStart.y,
                    "Z"
                ].join(" ");
            },
            showClientPieTooltip(client, event) {
                this.clientPieTooltip.content = this.getClientSaleTooltip(client);
                this.clientPieTooltip.visible = true;
                this.moveClientPieTooltip(event);
            },
            moveClientPieTooltip(event) {
                let chartWrap = event.currentTarget.closest(".donut-chart-wrap");
                if (chartWrap == null) {
                    throw new Error("Donut chart container is required");
                }
                let rect = chartWrap.getBoundingClientRect();
                this.clientPieTooltip.left = event.clientX - rect.left + 12;
                this.clientPieTooltip.top = event.clientY - rect.top + 12;
            },
            hideClientPieTooltip() {
                this.clientPieTooltip.visible = false;
            },
            scrollSaleChart(direction) {
                let chartScroll = this.$refs.saleChartScroll;
                if (chartScroll == null) {
                    throw new Error("Sale chart scroll container is required");
                }
                chartScroll.scrollLeft += direction * chartScroll.clientWidth;
            },
            handleSaleChartWheel(event) {
                if (this.selectedYear !== null) {
                    return;
                }
                let chartScroll = this.$refs.saleChartScroll;
                if (chartScroll == null) {
                    throw new Error("Sale chart scroll container is required");
                }
                let scrollDistance = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
                chartScroll.scrollLeft += scrollDistance;
                event.preventDefault();
            },
            getTaxYearStart() {
                if (this.userSetting == null || this.userSetting.taxYearStart == null || this.userSetting.taxYearStart === "") {
                    throw new Error("Tax year start is required");
                }
                let taxYearStartDate = new Date(parseInt(this.userSetting.taxYearStart, 10) * 1000);
                return {
                    month: taxYearStartDate.getMonth() + 1,
                    day: taxYearStartDate.getDate()
                };
            },
            getCurrentFiscalYearEnd() {
                let taxYearStart = this.getTaxYearStart();
                let currentDate = new Date();
                if (taxYearStart.month === 1 && taxYearStart.day === 1) {
                    return currentDate.getFullYear();
                }
                let currentYearStartDate = new Date(currentDate.getFullYear(), taxYearStart.month - 1, taxYearStart.day);
                if (currentDate.getTime() >= currentYearStartDate.getTime()) {
                    return currentDate.getFullYear() + 1;
                }
                return currentDate.getFullYear();
            },
            getFiscalYearDateRange(fiscalYearEnd) {
                let taxYearStart = this.getTaxYearStart();
                let startYear = fiscalYearEnd - 1;
                let endYear = fiscalYearEnd;
                if (taxYearStart.month === 1 && taxYearStart.day === 1) {
                    startYear = fiscalYearEnd;
                    endYear = fiscalYearEnd + 1;
                }
                return {
                    startDate: new Date(startYear, taxYearStart.month - 1, taxYearStart.day),
                    endDate: new Date(endYear, taxYearStart.month - 1, taxYearStart.day)
                };
            },
            getMonthValue(year, month) {
                return year * 12 + month;
            },
            getMonthValueFromDate(date) {
                return this.getMonthValue(date.getFullYear(), date.getMonth() + 1);
            },
            getPreviousDate(date) {
                return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
            },
            getPeriodName(year, month) {
                return year + "-" + String(month).padStart(2, "0");
            },
            getSelectedFiscalTableEndMonthValue() {
                let fiscalRange = this.getFiscalYearDateRange(this.selectedYear);
                let startMonthValue = this.getMonthValueFromDate(fiscalRange.startDate);
                let fiscalEndMonthValue = this.getMonthValueFromDate(this.getPreviousDate(fiscalRange.endDate));
                let currentMonthValue = this.getMonthValueFromDate(new Date());
                if (currentMonthValue < startMonthValue) {
                    return null;
                }
                if (currentMonthValue > fiscalEndMonthValue) {
                    return fiscalEndMonthValue;
                }
                return currentMonthValue;
            },
            changeDateIncrement(dateIncrement) {
                this.dateIncrement = dateIncrement;
                this.queryReportSaleStatistics();
            },
            queryReportData() {
                this.queryReportSaleStatistics();
                this.queryAnnualSalesByClient();
            },
            queryReportSaleStatistics() {
                this.startLoading();
                let params = {
                    type: 0,
                    companyUUID: getCurrentCompanyUUID(),
                    groupType: this.groupType
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                this.$axios.post('query_invoice_sale_statistics', {
                    params: params
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.reportBarList = this.buildSalePeriodList(response.data.dataList);
                        this.reportSummary = this.buildSaleSummary(this.reportBarList);
                    }
                    this.finishLoading();
                }).catch(error => {
                    this.finishLoading();
                    throw error;
                })
            },
            queryAnnualSalesByClient() {
                this.startLoading();
                let params = {
                    companyUUID: getCurrentCompanyUUID()
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                this.$axios.post('query_annual_sales_by_client', {
                    params: params
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.clientSalesList = this.buildClientSalesList(response.data.dataList);
                    }
                    this.finishLoading();
                }).catch(error => {
                    this.finishLoading();
                    throw error;
                })
            },
            queryTotalOutstandingBalance() {
                this.startLoading();
                this.$axios.post('query_total_outstanding_balance', {
                    params: {}
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.outstandingBalanceList = response.data.dataList;
                    }
                    this.finishLoading();
                }).catch(error => {
                    this.finishLoading();
                    throw error;
                })
            },
            buildSalePeriodList(dataList) {
                if (this.selectedYear === null && this.groupType === "MONTH") {
                    return this.buildAllYearMonthPeriodList(dataList);
                }
                if (this.selectedYear === null && this.groupType === "QUARTER") {
                    return this.buildAllYearQuarterPeriodList(dataList);
                }
                if (this.selectedYear !== null && this.groupType === "MONTH") {
                    return this.buildSelectedFiscalMonthPeriodList(dataList);
                }
                if (this.selectedYear !== null && this.groupType === "QUARTER") {
                    let quarterMap = {};
                    for (let i = 0; i < dataList.length; i++) {
                        quarterMap[dataList[i].fiscalQuarter] = dataList[i];
                    }
                    let quarterList = [];
                    for (let quarter = 4; quarter >= 1; quarter--) {
                        if (quarterMap[quarter] == null) {
                            quarterList.push({
                                periodKey: this.selectedYear + "-Q" + quarter,
                                periodName: this.selectedYear + "-Q" + quarter,
                                title: this.selectedYear + "-Q" + quarter,
                                periodYear: this.selectedYear,
                                periodMonth: null,
                                fiscalQuarter: quarter,
                                invoiceCount: 0,
                                totalMoney: 0
                            });
                        } else {
                            quarterList.push({
                                periodKey: this.selectedYear + "-Q" + quarter,
                                periodName: this.selectedYear + "-Q" + quarter,
                                title: this.selectedYear + "-Q" + quarter,
                                periodYear: this.selectedYear,
                                periodMonth: quarterMap[quarter].periodMonth,
                                fiscalQuarter: quarter,
                                invoiceCount: quarterMap[quarter].invoiceCount,
                                totalMoney: quarterMap[quarter].totalMoney
                            });
                        }
                    }
                    return quarterList;
                }
                return dataList.map(item => {
                    return this.buildSalePeriod(item);
                });
            },
            buildAllYearMonthPeriodList(dataList) {
                if (dataList.length === 0) {
                    return [];
                }
                let periodMap = {};
                let minPeriodValue = null;
                let maxPeriodValue = null;
                for (let i = 0; i < dataList.length; i++) {
                    let item = dataList[i];
                    let periodValue = this.getMonthValue(item.periodYear, item.periodMonth);
                    periodMap[periodValue] = item;
                    if (minPeriodValue === null || periodValue < minPeriodValue) {
                        minPeriodValue = periodValue;
                    }
                    if (maxPeriodValue === null || periodValue > maxPeriodValue) {
                        maxPeriodValue = periodValue;
                    }
                }
                let periodList = [];
                for (let periodValue = maxPeriodValue; periodValue >= minPeriodValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 12);
                    let periodMonth = periodValue - periodYear * 12;
                    let periodName = this.getPeriodName(periodYear, periodMonth);
                    if (periodMap[periodValue] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: periodMonth,
                            fiscalQuarter: null,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodValue]));
                    }
                }
                return periodList;
            },
            buildSelectedFiscalMonthPeriodList(dataList) {
                let fiscalRange = this.getFiscalYearDateRange(this.selectedYear);
                let startMonthValue = this.getMonthValueFromDate(fiscalRange.startDate);
                let endMonthValue = this.getMonthValueFromDate(this.getPreviousDate(fiscalRange.endDate));
                let periodMap = {};
                for (let i = 0; i < dataList.length; i++) {
                    periodMap[dataList[i].periodName] = dataList[i];
                }
                let periodList = [];
                for (let periodValue = endMonthValue; periodValue >= startMonthValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 12);
                    let periodMonth = periodValue - periodYear * 12;
                    let periodName = this.getPeriodName(periodYear, periodMonth);
                    if (periodMap[periodName] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: periodMonth,
                            fiscalQuarter: null,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodName]));
                    }
                }
                return periodList;
            },
            buildAllYearQuarterPeriodList(dataList) {
                if (dataList.length === 0) {
                    return [];
                }
                let periodMap = {};
                let minPeriodValue = null;
                let maxPeriodValue = null;
                for (let i = 0; i < dataList.length; i++) {
                    let item = dataList[i];
                    let periodValue = item.periodYear * 4 + item.fiscalQuarter;
                    periodMap[periodValue] = item;
                    if (minPeriodValue === null || periodValue < minPeriodValue) {
                        minPeriodValue = periodValue;
                    }
                    if (maxPeriodValue === null || periodValue > maxPeriodValue) {
                        maxPeriodValue = periodValue;
                    }
                }
                let periodList = [];
                for (let periodValue = maxPeriodValue; periodValue >= minPeriodValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 4);
                    let fiscalQuarter = periodValue - periodYear * 4;
                    let periodName = periodYear + "-Q" + fiscalQuarter;
                    if (periodMap[periodValue] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: null,
                            fiscalQuarter: fiscalQuarter,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodValue]));
                    }
                }
                return periodList;
            },
            buildSalePeriod(item) {
                return {
                    periodKey: item.periodName,
                    periodName: item.periodName,
                    title: item.periodName,
                    periodYear: item.periodYear,
                    periodMonth: item.periodMonth,
                    fiscalQuarter: item.fiscalQuarter,
                    invoiceCount: item.invoiceCount,
                    totalMoney: item.totalMoney
                };
            },
            buildClientSalesList(dataList) {
                let sortedDataList = dataList.slice().sort((firstClient, secondClient) => {
                    return Number(secondClient.totalMoney) - Number(firstClient.totalMoney);
                }).slice(0, 5);
                let totalMoney = 0;
                for (let i = 0; i < sortedDataList.length; i++) {
                    totalMoney += Number(sortedDataList[i].totalMoney);
                }
                return sortedDataList.map((item, index) => {
                    let percent = 0;
                    if (totalMoney > 0) {
                        percent = Math.round(Number(item.totalMoney) / totalMoney * 1000) / 10;
                    }
                    return {
                        innerUUID: item.innerUUID,
                        name: item.name,
                        totalMoney: item.totalMoney,
                        percent: percent,
                        color: this.clientColorList[index % this.clientColorList.length]
                    };
                });
            },
            buildSaleSummary(periodList) {
                let totalInvoiceCount = 0;
                let totalMoney = 0;
                for (let i = 0; i < periodList.length; i++) {
                    totalInvoiceCount += Number(periodList[i].invoiceCount);
                    totalMoney += Number(periodList[i].totalMoney);
                }
                return {
                    totalInvoiceCount: totalInvoiceCount,
                    totalMoney: totalMoney
                };
            }
        },
        created() {
            this.selectedYear = this.getCurrentFiscalYearEnd();
        },
        mounted() {
            this.queryReportData();
            this.queryTotalOutstandingBalance();
        }
    }
</script>

<style scoped>
    /deep/ .el-header {
        background-color: #ffffff;
        font-size: 25px;
        font-weight: 700;
        height: 100px !important;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 30px;
        border-bottom: 1px solid #E4E7ED;
    }

    .report-main {
        padding: 0;
        background: #F6F7F9;
    }

    .report-tabs {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 26px 30px 0;
        background: #ffffff;
        border-bottom: 1px solid #E4E7ED;
    }

    .report-tab {
        padding: 0 0 14px;
        color: #5F6368;
        font-size: 16px;
        font-weight: 600;
        background: transparent;
        border: none;
        border-bottom: 4px solid transparent;
        cursor: pointer;
    }

    .report-tab.active {
        color: #111111;
        border-bottom-color: #FF4B1F;
    }

    .report-overview-section {
        padding-bottom: 24px;
    }

    .report-overview-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 144px;
    }

    .report-panel {
        min-width: 0;
    }

    .report-section {
        padding: 32px 30px;
        background: #ffffff;
        border-bottom: 12px solid #F0F2F5;
    }

    .report-section h2 {
        margin: 0 0 10px;
        color: #111111;
        font-size: 22px;
        font-weight: 700;
    }

    .report-section-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    }

    .invoice-count {
        color: #111111;
        font-size: 15px;
        font-weight: 600;
    }

    .total-sales {
        margin-top: 6px;
        color: #000000;
        font-size: 36px;
        font-weight: 700;
    }

    .year-select {
        width: 150px;
    }

    .report-tabs .year-select {
        transform: translateY(-14px);
    }

    .report-mode-tabs {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }

    .mode-button {
        padding: 8px 14px;
        color: #5F6368;
        font-size: 15px;
        background: #F5F6F8;
        border: 1px solid #F0F1F3;
        cursor: pointer;
    }

    .mode-button.active {
        color: #ffffff;
        background: #333333;
        border-color: #333333;
    }

    .bar-chart-wrap {
        margin-top: 20px;
    }

    .bar-chart-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-bottom: 8px;
    }

    .bar-chart-scroll {
        max-width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
    }

    .bar-chart {
        display: grid;
        gap: 3px;
        align-items: end;
        min-height: 310px;
        padding-bottom: 2px;
        position: relative;
    }

    .bar-chart:before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 38px;
        height: 1px;
        background: #DADDE3;
    }

    .bar-column {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        min-width: 0;
        position: relative;
        z-index: 1;
    }

    .bar-track {
        height: 260px;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        width: 100%;
        cursor: pointer;
    }

    .bar-value {
        width: 72px;
        background: #217BD3;
        transition: height 0.18s ease;
    }

    .bar-label {
        height: 36px;
        padding-top: 10px;
        box-sizing: border-box;
        color: #737373;
        font-size: 14px;
    }

    .period-list {
        margin-top: 34px;
        max-height: 500px;
        overflow-y: auto;
    }

    .empty-report {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        margin-top: 20px;
        color: #737373;
        font-size: 15px;
        border: 1px dashed #DADDE3;
    }

    .period-row,
    .client-sales-row,
    .outstanding-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 50px;
        border-bottom: 1px solid #E0E2E6;
        color: #111111;
        font-size: 16px;
        font-weight: 600;
    }

    .client-chart-row {
        display: grid;
        grid-template-columns: 220px minmax(220px, 1fr);
        gap: 34px;
        align-items: center;
    }

    .donut-chart {
        width: 200px;
        height: 200px;
    }

    .donut-chart-wrap {
        width: 200px;
        height: 200px;
        position: relative;
    }

    .donut-empty {
        fill: none;
        stroke-width: 56;
        stroke: #E5E7EB;
    }

    .donut-slice {
        cursor: pointer;
    }

    .client-pie-tooltip {
        position: absolute;
        z-index: 10;
        padding: 8px 10px;
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        background: rgba(51, 51, 51, 0.94);
        border-radius: 4px;
        pointer-events: none;
    }

    .client-sales-list {
        min-width: 0;
    }

    .client-color {
        width: 4px;
        height: 36px;
        margin-right: 14px;
    }

    .client-name {
        flex: 1;
    }

    .outstanding-total {
        margin-top: 14px;
        color: #000000;
        font-size: 36px;
        font-weight: 700;
    }

    .outstanding-list {
        margin-top: 20px;
        max-height: 500px;
        overflow-y: auto;
    }

    @media (max-width: 1100px) {
        .report-overview-grid {
            grid-template-columns: 1fr;
        }
    }
</style>
